/**
 * CustomMetricsCategoryPanel (Story 37.5 — Phase 2 category panel #4)
 *
 * The `custom-metrics` sidebar category's floating-panel body. Replaces the
 * generic `CardListPanel` (which dropped a blank `custom-metric` widget the user
 * then had to configure) with a purpose-built **configure-then-add** builder:
 * the user defines the formula + variables + output format FIRST, then Add
 * attaches the populated config to a new `custom-metric` widget so it renders
 * non-blank (AC #1, AC #6).
 *
 * Reuse, don't fork: the formula/variables/format controls are the SHARED
 * `CustomMetricConfigSection` from `widgetDataForms` — the exact same sub-form
 * the `WidgetConfigPanel` uses when editing an existing custom-metric (AC #4
 * anti-drift). The sub-form's contract is `{ widget, onUpdate }`; this panel has
 * no real widget yet, so it wraps a local **draft config** in a synthetic
 * draft-widget and adapts `onUpdate` to merge patches into the draft (Pre-mortem
 * A). It renders the sub-form WITHOUT `groupedMetricOptions` so the static
 * catalog default is used — client-connected narrowing stays in
 * `WidgetConfigPanel` and is deferred to Story 37.7 (AC #9).
 *
 * Add is DISABLED until the draft formula is present AND valid
 * (`isCustomMetricDraftValid`, which calls the SAME `validateFormulaExpression`
 * the live badge uses) — the concrete "never blank" enforcement: there is no
 * path from this panel to a blank/invalid custom-metric widget (AC #7).
 *
 * Single-type panel (NO type selector): `custom-metric` is one widget type, so
 * this mirrors `ImagesCategoryPanel`, NOT the four-way `ContentBlocksCategoryPanel`
 * (AC #8).
 */

import { useState, useMemo, useCallback } from "react";
import { Calculator } from "lucide-react";
import { FloppyDisk } from "@phosphor-icons/react";
import type { WidgetConfig } from "../../lib/templateUtils";
import { DEFAULT_WIDGET_CONFIGS, DEFAULT_WIDGET_TITLES } from "./canvasWidgetDefaults";
import { CustomMetricConfigSection, isCustomMetricDraftValid } from "./widgetDataForms";
import type { CategoryPanelProps } from "./categoryPickerRegistry";
import {
  useClientCustomMetrics,
  useSaveCustomMetric,
  type SavedCustomMetric,
} from "../../hooks/useClientCustomMetrics";
import {
  useClientMetricSuggestions,
  type SuggestedMetric,
} from "../../hooks/useClientMetricSuggestions";
import {
  CUSTOM_METRIC_TEMPLATES,
  type CustomMetricTemplate,
} from "../../lib/customMetricTemplates";
import { expandCrossPlatformSummaryPreset } from "../../lib/widgetPresets";

/** Synthetic draft-widget id — stable, never persisted (Pre-mortem A). */
const DRAFT_CUSTOM_METRIC_WIDGET_ID = "__draft_custom_metric__";

export function CustomMetricsCategoryPanel({
  onWidgetAdd,
  clientId,
  onPresetWidgetsAdd,
}: CategoryPanelProps) {
  // Seed the draft from the real default config so a configured-then-added
  // custom-metric equals what a blank drop + manual fill would produce.
  // structuredClone (not a shallow spread) so nested defaults (e.g. the
  // `variables` array) aren't shared by reference with the module-level
  // DEFAULT_WIDGET_CONFIGS across add/reset cycles (code review pass 1).
  const [draft, setDraft] = useState<Record<string, unknown>>(() =>
    structuredClone(DEFAULT_WIDGET_CONFIGS["custom-metric"]),
  );

  // Synthetic draft-widget wrapping the local draft. The sub-form only reads
  // `id` + `config`, but TS needs the full WidgetConfig shape, so fill the rest
  // with throwaway-but-valid defaults (never used by CustomMetricConfigSection).
  const draftWidget = useMemo<WidgetConfig>(
    () => ({
      id: DRAFT_CUSTOM_METRIC_WIDGET_ID,
      type: "custom-metric" as const,
      title: DEFAULT_WIDGET_TITLES["custom-metric"],
      position: { row: 0, col: 0, colSpan: 6, rowSpan: 3 },
      config: draft,
    }),
    [draft],
  );

  // Adapt the sub-form's `onUpdate(id, changes)` to merge `changes.config` into
  // the local draft (Pre-mortem A: the merge MUST land or Add stays disabled
  // forever). The id is ignored — there is only one draft.
  const handleDraftUpdate = useCallback(
    (_id: string, changes: Partial<WidgetConfig>) => {
      if (!changes.config) return;
      setDraft((d) => ({ ...d, ...(changes.config as Record<string, unknown>) }));
    },
    [],
  );

  // Add is enabled only once the formula is usefully configured (AC #7) AND a
  // real add handler is wired — a missing `onWidgetAdd` would otherwise leave a
  // button that wipes the draft and does nothing (37.3 code-review R1 lesson).
  const canAdd = isCustomMetricDraftValid(draft) && typeof onWidgetAdd === "function";

  const handleAdd = useCallback(() => {
    if (!canAdd || !onWidgetAdd) return;
    const added = onWidgetAdd("custom-metric", structuredClone(draft));
    // Reset the draft ONLY if the widget was actually added — a rejected add
    // (page at MAX_WIDGETS) must not discard the user's work (37.3 R1).
    if (added) setDraft(structuredClone(DEFAULT_WIDGET_CONFIGS["custom-metric"]));
  }, [canAdd, onWidgetAdd, draft]);

  // Story 16.3 — save the built formula as a reusable metric + load a saved one.
  // `clientId` is the editor's effective client (undefined in the mock/
  // unconnected state → save/load disabled, mirroring GoalsCategoryPanel).
  const { data: savedMetrics = [] } = useClientCustomMetrics(clientId);
  const saveMetric = useSaveCustomMetric(clientId);
  const canSave =
    isCustomMetricDraftValid(draft) && !!clientId && !saveMetric.isPending;

  const handleSave = useCallback(() => {
    if (!clientId || !isCustomMetricDraftValid(draft)) return;
    const cfg = draft as Record<string, unknown>;
    const variables =
      (cfg.variables as SavedCustomMetric["sourceMetrics"]) ?? [];
    // Story 16.4 — variables carrying a platform binding are union members;
    // record them in metric_mappings (the structured cross-platform-union row).
    const metricMappings = variables
      .filter((v) => typeof v.platform === "string" && v.platform)
      .map((v) => ({
        integrationType: v.platform as string,
        fieldName: v.metricKey,
      }));
    saveMetric.mutate({
      name: ((cfg.title as string) || "").trim() || "Custom metric",
      formulaExpression: (cfg.formulaExpression as string) ?? "",
      sourceMetrics: variables,
      outputFormat:
        (cfg.outputFormat as SavedCustomMetric["outputFormat"]) ?? "number",
      ...(metricMappings.length > 0 ? { metricMappings } : {}),
    });
  }, [clientId, draft, saveMetric]);

  const handleLoad = useCallback(
    (metricId: string) => {
      const m = savedMetrics.find((x) => x.id === metricId);
      if (!m) return;
      // Map the saved metric back onto the draft's identity fields; per-widget
      // display fields (decimalPlaces, currencyCode, …) keep their draft values.
      setDraft((d) => ({
        ...d,
        title: m.name,
        formulaExpression: m.formulaExpression,
        variables: m.sourceMetrics,
        outputFormat: m.outputFormat,
      }));
    },
    [savedMetrics]
  );

  // Story 16.4 — cross-platform union suggestions; applying one builds a SUM
  // formula whose operand variables each carry their own platform.
  const { data: suggestions = [] } = useClientMetricSuggestions(clientId);
  const handleApplySuggestion = useCallback((s: SuggestedMetric) => {
    setDraft((d) => ({
      ...d,
      title: s.label,
      variables: s.members.map((m, i) => ({
        id: `m${i}`,
        label: m.label,
        metricKey: m.metricKey,
        platform: m.platform,
      })),
      formulaExpression: s.members.map((_, i) => `m${i}`).join(" + "),
      outputFormat: s.outputFormat,
    }));
  }, []);

  // Story 16.7 — seed the draft from a static template; the user then re-binds
  // metricKeys for their own integrations in the builder below.
  const handleApplyTemplate = useCallback((tpl: CustomMetricTemplate) => {
    setDraft((d) => ({
      ...d,
      title: tpl.label,
      formulaExpression: tpl.formulaExpression,
      variables: tpl.variables.map((v) => ({ ...v })),
      outputFormat: tpl.outputFormat,
    }));
  }, []);

  // Story 16.5 — drop ALL suggested cross-platform totals as one summary (a
  // heading + a custom-metric KPI per total) in ONE atomic add. A preset, not a
  // new widget type.
  const handleAddSummary = useCallback(() => {
    if (typeof onPresetWidgetsAdd !== "function" || suggestions.length === 0) return;
    onPresetWidgetsAdd(
      expandCrossPlatformSummaryPreset(
        suggestions.map((s) => ({
          label: s.label,
          outputFormat: s.outputFormat,
          members: s.members.map((m) => ({
            label: m.label,
            metricKey: m.metricKey,
            platform: m.platform,
          })),
        }))
      )
    );
  }, [onPresetWidgetsAdd, suggestions]);

  return (
    <div className="flex h-full flex-col p-3" data-testid="custom-metrics-category-panel">
      <div className="mb-3 flex items-center gap-2">
        <Calculator size={18} className="text-muted-foreground" />
        <h3 className="text-[13px] font-semibold text-foreground">Custom Metrics</h3>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Story 16.7 — start from a pre-built template. */}
        <select
          aria-label="Start from a custom-metric template"
          data-testid="custom-metrics-template-select"
          defaultValue=""
          onChange={(e) => {
            const tpl = CUSTOM_METRIC_TEMPLATES.find((t) => t.key === e.target.value);
            if (tpl) {
              handleApplyTemplate(tpl);
              e.currentTarget.value = "";
            }
          }}
          className="mb-3 h-9 w-full rounded-lg border-[0.5px] border-input bg-background px-2 text-[13px] shadow-xs"
        >
          <option value="">Start from a template…</option>
          {CUSTOM_METRIC_TEMPLATES.map((t) => (
            <option key={t.key} value={t.key}>
              {t.label} — {t.description}
            </option>
          ))}
        </select>

        <CustomMetricConfigSection widget={draftWidget} onUpdate={handleDraftUpdate} />

        {/* Story 16.3 — save the built formula as a reusable metric + load a saved one. */}
        <div className="mt-4 border-t-[0.5px] border-input/70 pt-4">
          <h4 className="mb-2 text-[12px] font-medium text-muted-foreground">
            Reusable metrics
          </h4>
          {clientId ? (
            <div className="space-y-2">
              {savedMetrics.length > 0 && (
                <select
                  aria-label="Load a saved custom metric"
                  data-testid="custom-metrics-load-select"
                  defaultValue=""
                  onChange={(e) => {
                    if (e.target.value) handleLoad(e.target.value);
                  }}
                  className="h-9 w-full rounded-lg border-[0.5px] border-input bg-background px-2 text-[13px] shadow-xs"
                >
                  <option value="">Load a saved metric…</option>
                  {savedMetrics.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              )}
              <button
                type="button"
                data-testid="custom-metrics-save-btn"
                disabled={!canSave}
                onClick={handleSave}
                className="flex h-9 w-full items-center justify-center gap-1.5 rounded-lg border-[0.5px] border-input bg-background px-3 text-[13px] font-medium shadow-xs transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FloppyDisk size={14} />
                {saveMetric.isPending ? "Saving…" : "Save as reusable"}
              </button>
              {saveMetric.isError && (
                <p className="text-[12px] text-destructive">
                  {(saveMetric.error as Error)?.message ?? "Save failed"}
                </p>
              )}
            </div>
          ) : (
            <p className="text-[12px] text-muted-foreground">
              Connect a client to save and reuse custom metrics.
            </p>
          )}
        </div>

        {/* Story 16.4 — suggested cross-platform totals (union metrics). */}
        {clientId && suggestions.length > 0 && (
          <div className="mt-4 border-t-[0.5px] border-input/70 pt-4">
            <h4 className="mb-2 text-[12px] font-medium text-muted-foreground">
              Suggested totals
            </h4>
            <div className="space-y-1.5">
              {suggestions.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  data-testid={`custom-metrics-suggestion-${s.key}`}
                  onClick={() => handleApplySuggestion(s)}
                  className="flex w-full flex-col items-start gap-0.5 rounded-lg border-[0.5px] border-input bg-background px-3 py-2 text-left shadow-xs transition-colors hover:bg-accent"
                >
                  <span className="text-[13px] font-medium text-foreground">
                    {s.label}
                  </span>
                  <span className="text-[11px] text-muted-foreground">
                    {s.members.map((m) => m.platformLabel).join(" + ")}
                  </span>
                </button>
              ))}
            </div>
            {typeof onPresetWidgetsAdd === "function" && (
              <button
                type="button"
                data-testid="custom-metrics-add-summary-btn"
                onClick={handleAddSummary}
                className="mt-2 w-full rounded-lg border-[0.5px] border-input bg-primary px-3 py-2 text-[13px] font-medium text-primary-foreground shadow-xs transition-colors hover:bg-primary/90"
              >
                Add all as a Cross-Platform Summary
              </button>
            )}
          </div>
        )}
      </div>

      <button
        type="button"
        data-testid="custom-metrics-panel-add-btn"
        disabled={!canAdd}
        onClick={handleAdd}
        className="mt-3 w-full rounded-md border border-input bg-primary px-3 py-2 text-[13px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Add to report
      </button>
    </div>
  );
}
